/** HR取込(人事CSV)。旧 Jinja 画面と同じ「判定 → 反映」の2段。
 *
 * 判定では何も書かない。サーバに CSV 本文を預けて引換券(token)を受け取り、
 * 「反映する」で引換券を返して初めて書き込む。エラー行は飛ばして追加・更新だけ入る。
 */
import { InboxOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Flex,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import type { ImportResult, ImportRow } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { importStatusColor, importStatusLabel } from "./constants";
import { errorText, useHrPerms } from "./hooks";
import { useDownload } from "./loanHooks";

const { Paragraph, Text } = Typography;

const COLUMNS: ColumnsType<ImportRow> = [
  { title: "行", dataIndex: "line", width: 90 },
  { title: "社員番号", dataIndex: "emp_code", width: 120, render: (v: string) => v || "—" },
  { title: "氏名", dataIndex: "name", width: 160, render: (v: string) => v || "—" },
  { title: "メールアドレス", dataIndex: "email", width: 240, render: (v: string) => v || "—" },
  {
    title: "判定",
    dataIndex: "status",
    width: 110,
    render: (v: string) => <Tag color={importStatusColor(v)}>{importStatusLabel(v)}</Tag>,
  },
  {
    title: "差分・エラー内容",
    width: 320,
    ellipsis: true,
    onCell: (row) => ({ title: row.status === "error" ? row.error : row.diffs.join(" / ") || "—" }),
    render: (_: unknown, row: ImportRow) =>
      row.status === "error" ? (
        <Text type="danger">{row.error}</Text>
      ) : (
        <Text type="secondary">{row.diffs.join(" / ") || "—"}</Text>
      ),
  },
];

function Summary({ result }: { result: ImportResult }) {
  return (
    <Space size="small" wrap>
      <Tag color="green">追加 {result.added}</Tag>
      <Tag color="blue">更新 {result.updated}</Tag>
      <Tag>変更なし {result.unchanged}</Tag>
      <Tag color={result.errors ? "red" : "default"}>エラー {result.errors}</Tag>
    </Space>
  );
}

export function ImportPage() {
  const api = useApi();
  const qc = useQueryClient();
  const download = useDownload();
  const { canWrite } = useHrPerms();
  const [file, setFile] = useState<File | null>(null);
  const [judged, setJudged] = useState<ImportResult | null>(null);
  const [applied, setApplied] = useState<ImportResult | null>(null);

  const judge = useMutation({
    mutationFn: (f: File) => api.judgeHrImport(f),
    onSuccess: (res) => {
      setApplied(null);
      setJudged(res);
    },
    onError: (e: unknown) => void message.error(errorText(e)),
  });

  const apply = useMutation({
    mutationFn: (token: string) => api.applyHrImport(token),
    onSuccess: (res) => {
      setApplied(res);
      setJudged(null);
      setFile(null);
      void message.success(`取り込んだ(追加 ${res.added} / 更新 ${res.updated})`);
      void qc.invalidateQueries({ queryKey: ["hr"] });
      void qc.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (e: unknown) => {
      void message.error(errorText(e));
      setJudged(null); // 引換券は使い切り。やり直しは判定から
    },
  });

  const willWrite = (judged?.added ?? 0) + (judged?.updated ?? 0);

  const confirmApply = () => {
    if (!judged?.token) return;
    Modal.confirm({
      title: "この内容で取り込む",
      content: `エラー以外の ${willWrite} 行を反映する。よろしいですか?`,
      okText: "反映する",
      cancelText: "やめる",
      onOk: () => apply.mutateAsync(judged.token),
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3}>HR取込(人事CSV)</Typography.Title>
      <Paragraph type="secondary" style={{ maxWidth: 900 }}>
        1行目は見出し。列は emp_code, family_name, given_name, family_name_kana,
        given_name_kana, email, department, employment_type, org_unit, position,
        manager_emp_code, work_mobile, work_phone, extension(必須は emp_code, family_name(または name)。新しい人は employment_type も必須。email は任意、順序は
        自由・知らない列は無視)。日本語の見出し(社員番号 / 姓 / 名 / 姓カナ / 名カナ /
        メールアドレス …)でも読める。<Text strong>旧形式の name(氏名)1列も受け付ける</Text>
        (最初の空白で姓名に割る。割れない行は「要確認」になる)。
        突き合わせは emp_code で行い、無ければ追加・あれば更新する。
        <Text strong>在籍状態は取込では変えない</Text>
        (入退社は手続きから。新しく追加した人は「入社前」で登録する)。文字コードは
        UTF-8(BOM可)か cp932。空欄の列は「変更しない」。org_unit は組織図の経路
        (会社を除いた「本部 / 部」の形)、position は役職名、manager_emp_code は上長の
        社員番号(同じCSVで追加する人も指定できる)。work_mobile / work_phone / extension は
        会社携帯 / 会社電話 / 内線(見出しは「会社携帯」「電話」「内線」でもよい)。
        <Text strong>電話の欄を消すときは「-」だけを書く</Text>(空欄は「変更しない」)。
      </Paragraph>

      {!canWrite && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="取込には hr:operator 以上が必要"
          description="閲覧だけの権限では判定も反映もできない。"
        />
      )}

      <Card
        size="small"
        title="CSVを選ぶ"
        style={{ marginBottom: 16 }}
        extra={
          <Button size="small" onClick={() => void download("/api/hr/import/sample")}>
            見本CSVを落とす
          </Button>
        }
      >
        <Upload.Dragger
          accept=".csv,text/csv"
          maxCount={1}
          disabled={!canWrite}
          fileList={file ? [{ uid: "1", name: file.name }] : []}
          beforeUpload={(f) => {
            setFile(f);
            setJudged(null);
            setApplied(null);
            return false; // 自動で送らない。押されたときだけ判定する
          }}
          onRemove={() => {
            setFile(null);
            setJudged(null);
            return true;
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">ここにCSVを置く、またはクリックして選ぶ</p>
          <p className="ant-upload-hint">選んでも書き込まない(まず判定だけ出す)</p>
        </Upload.Dragger>
        <Flex gap={8} align="center" style={{ marginTop: 12 }}>
          <Button
            type="primary"
            disabled={!file || !canWrite}
            loading={judge.isPending}
            onClick={() => file && judge.mutate(file)}
          >
            内容を確認
          </Button>
          <Text type="secondary">押しても書き込まない(まず判定だけ出す)</Text>
        </Flex>
      </Card>

      {applied && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          message="取り込んだ"
          description={
            <Space direction="vertical" size={4}>
              <Summary result={applied} />
              <span>
                <Link to="/hr/employees">従業員一覧</Link> で確認できる。
              </span>
            </Space>
          }
        />
      )}

      {judged && (
        <Card
          size="small"
          title={
            <Space>
              <span>判定</span>
              <Summary result={judged} />
            </Space>
          }
          extra={
            <Space>
              <Text type="secondary">エラー行は飛ばして、追加・更新の行だけ反映する</Text>
              <Button
                type="primary"
                disabled={!willWrite || !canWrite}
                loading={apply.isPending}
                onClick={confirmApply}
              >
                反映する
              </Button>
            </Space>
          }
        >
          {(judged.warnings ?? []).length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="このファイルについての注意"
              description={
                <Space direction="vertical" size={2}>
                  {(judged.warnings ?? []).map((w) => (
                    <span key={w}>{w}</span>
                  ))}
                </Space>
              }
            />
          )}
          <Descriptions size="small" column={2} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="ファイル">{file?.name ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="判定の有効期限">
              30分(過ぎたらもう一度「内容を確認」から)
            </Descriptions.Item>
          </Descriptions>
          <Table<ImportRow>
            scroll={{ x: "max-content" }}
            rowKey="line"
            size="small"
            columns={COLUMNS}
            dataSource={judged.rows}
            pagination={false}
            locale={{ emptyText: "行が無い" }}
          />
        </Card>
      )}
    </div>
  );
}
